// Edit your own running plan (plan v4 item 1): which days, what kind of run, and a
// target in miles or minutes. Plus the optional "Change next run" sheet. Saving
// writes the plan that Today and the Running tab both read.
import { RUN_TYPES, cleanSchedule, changeTarget, newRunId, targetText, runTitle, STEP_MI } from '../lib/run-schedule.js';
import { saveRunSchedule } from '../lib/grit-data.js';
import { toast } from './toast.js';
import { icon } from './icons.js';

const WD = [[1, 'Mon'], [2, 'Tue'], [3, 'Wed'], [4, 'Thu'], [5, 'Fri'], [6, 'Sat'], [7, 'Sun']];
let overlay = null;
function close() { if (overlay) { overlay.remove(); overlay = null; } }
function shell(inner) {
  close();
  overlay = document.createElement('div');
  overlay.className = 'fixed inset-0 z-[120] flex items-end justify-center bg-black/30';
  overlay.innerHTML = `<div class="grit-sheet overflow-auto">${inner}</div>`;
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
  document.body.appendChild(overlay);
  return overlay;
}

export function openRunPlanEditor({ schedule, onSaved }) {
  let rows = cleanSchedule(schedule).map((r) => ({ ...r }));

  function paint() {
    const list = rows.map((r, i) => `
      <div class="bg-pagebg rounded-2xl p-3 mb-2">
        <div class="flex items-center gap-2">
          <select data-i="${i}" data-f="weekday" aria-label="Day" class="grit-input !w-auto flex-none !bg-card">${WD.map(([v, l]) => `<option value="${v}" ${r.weekday === v ? 'selected' : ''}>${l}</option>`).join('')}</select>
          <select data-i="${i}" data-f="type" aria-label="Kind of run" class="grit-input flex-1 min-w-0 !bg-card">${RUN_TYPES.map(([v, l]) => `<option value="${v}" ${r.type === v ? 'selected' : ''}>${l}</option>`).join('')}</select>
          <button data-rm="${i}" aria-label="Remove this run" class="flex-none w-11 h-11 -mr-1 flex items-center justify-center text-inksoft">${icon.x('w-4 h-4')}</button>
        </div>
        <div class="flex items-center gap-2 mt-2">
          <input data-i="${i}" data-f="value" type="number" inputmode="decimal" step="0.25" min="0.25" value="${r.value}" aria-label="Target" class="grit-input flex-1 text-center !bg-card font-semibold" />
          <div class="grit-seg flex-none w-[150px]">
            <button data-i="${i}" data-unit="mi" class="${r.unit === 'mi' ? 'on' : ''}">Miles</button>
            <button data-i="${i}" data-unit="min" class="${r.unit === 'min' ? 'on' : ''}">Minutes</button>
          </div>
        </div>
      </div>`).join('');
    overlay.firstElementChild.innerHTML = `
      <div class="grit-sheet-handle"></div>
      <div class="flex items-start justify-between">
        <div><div class="text-[20px] font-bold tracking-tight text-ink">Your running plan</div>
          <div class="text-[13px] text-inksoft">Today and Running both follow this. Nothing changes on its own.</div></div>
        <button data-close aria-label="Close" class="text-inksoft w-11 h-11 -mr-2 flex items-center justify-center flex-none">${icon.x('w-5 h-5')}</button>
      </div>
      <div class="mt-4">${list || '<div class="text-[15px] text-inksoft mb-3">No runs yet.</div>'}</div>
      <button id="add-run" class="grit-btn-quiet !text-grit">Add a run</button>
      <div class="text-[13px] text-inksoft mt-3">The book's cardio: keep runs off leg days, mostly easy, at most one hard session a week (p.333-336, 358).</div>
      <div class="flex gap-2 mt-4">
        <button data-close class="grit-btn-quiet flex-1">Cancel</button>
        <button id="save-plan" class="grit-btn flex-1">Save plan</button>
      </div>`;
    const q = (s) => overlay.querySelectorAll(s);
    q('[data-close]').forEach((b) => { b.onclick = close; });
    q('select[data-f], input[data-f]').forEach((n) => {
      n.onchange = () => {
        const r = rows[+n.dataset.i]; const f = n.dataset.f;
        r[f] = f === 'type' ? n.value : Number(n.value);
        if (f === 'type' && n.value === 'lowimpact' && r.unit === 'mi') { r.unit = 'min'; r.value = 30; paint(); }
      };
    });
    q('[data-unit]').forEach((b) => { b.onclick = () => { const r = rows[+b.dataset.i]; if (r.unit !== b.dataset.unit) { r.unit = b.dataset.unit; r.value = r.unit === 'min' ? 15 : 1; } paint(); }; });
    q('[data-rm]').forEach((b) => { b.onclick = () => { rows.splice(+b.dataset.rm, 1); paint(); }; });
    overlay.querySelector('#add-run').onclick = () => {
      const used = new Set(rows.map((r) => r.weekday));
      const wd = [6, 2, 3, 5, 1, 7, 4].find((d) => !used.has(d)) || 6;
      rows.push({ id: newRunId(), weekday: wd, type: 'easy', unit: 'mi', value: 1 });
      paint();
    };
    overlay.querySelector('#save-plan').onclick = async () => {
      const clean = cleanSchedule(rows);
      if (clean.length !== rows.length) { toast('Each run needs a day, a kind and a number above zero', { tone: 'bad' }); return; }
      const ok = await saveRunSchedule(clean);
      if (!ok) { toast("Couldn't save the plan, try again", { tone: 'bad' }); return; }
      close();
      toast('Plan saved');
      onSaved?.(clean);
    };
  }

  shell('');
  paint();
}

// Optional "Change next run": less, a +0.25 mi shortcut (distance targets only), or type it.
export function openChangeNextRun({ schedule, runId, onSaved }) {
  const all = cleanSchedule(schedule);
  const run = all.find((r) => r.id === runId);
  if (!run) return;
  let next = { ...run };

  function paint() {
    overlay.firstElementChild.innerHTML = `
      <div class="grit-sheet-handle"></div>
      <div class="text-[20px] font-bold tracking-tight text-ink">Change next run</div>
      <div class="text-[15px] text-inksoft mt-1">${runTitle(run)}: now ${targetText(run)}. It stays there unless you change it here.</div>
      <div class="font-rounded font-bold text-ink text-center tabular-nums mt-5" style="font-size:40px">${targetText(next)}</div>
      <div class="flex gap-2 mt-4">
        <button data-step="less" class="grit-btn-quiet flex-1">Less</button>
        ${next.unit === 'mi' && run.type !== 'test' ? `<button data-step="more" class="grit-btn-quiet flex-1">+${STEP_MI} mi</button>` : ''}
      </div>
      <label class="flex items-center gap-2 mt-3">
        <span class="text-[15px] text-inksoft flex-none">Or type it</span>
        <input id="typed" type="number" inputmode="decimal" step="0.25" min="0.25" placeholder="${next.value}" aria-label="New target" class="grit-input flex-1 text-center font-semibold" />
        <span class="text-[15px] text-inksoft flex-none">${next.unit === 'mi' ? 'mi' : 'min'}</span>
      </label>
      <div class="flex gap-2 mt-5">
        <button data-close class="grit-btn-quiet flex-1">Cancel</button>
        <button id="save-next" class="grit-btn flex-1">Save</button>
      </div>`;
    overlay.querySelectorAll('[data-close]').forEach((b) => { b.onclick = close; });
    overlay.querySelectorAll('[data-step]').forEach((b) => { b.onclick = () => { next = changeTarget(next, b.dataset.step); paint(); }; });
    const typed = overlay.querySelector('#typed');
    typed.oninput = () => { const v = Number(typed.value); if (v > 0) next = changeTarget(next, v); overlay.querySelector('.font-rounded').textContent = targetText(next); };
    overlay.querySelector('#save-next').onclick = async () => {
      const updated = all.map((r) => (r.id === run.id ? next : r));
      const ok = await saveRunSchedule(updated);
      if (!ok) { toast("Couldn't save, try again", { tone: 'bad' }); return; }
      close();
      toast(`Next ${runTitle(run).toLowerCase()}: ${targetText(next)}`);
      onSaved?.(updated);
    };
  }

  shell('');
  paint();
}
